<template>
    <PageHeader :title="form.updateStatus ? sprintf($t('pages.ecommerce.checkout.editOrderPattern'), [form.data.id ?? '']) : $t('pages.ecommerce.checkout.newOrder')" back-to="/ecommerce/checkout"/>

    <el-tabs v-model="tabModel" class="checkout-tabs">
        <el-tab-pane :label="$t('pages.ecommerce.checkout.save.tabs.general')" name="general">
            <el-form :model="form.data" ref="dataForm">
                <div class="row g-6 mb-6 g-xl-9 mb-xl-9 unset-position-card">
                    <div class="col-md-9">
                        <div class="card mb-5">
                            <div class="card-header border-bottom-dashed align-items-center">
                                <h4 class="card-title m-0">
                                    {{ $t('pages.ecommerce.checkout.save.generalCardTitle') }}
                                </h4>
                            </div>
                            <div class="card-body px-9 pt-4">
                                <div class="row">
                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.order_type_id') }}</label>
                                        <el-form-item prop="order_type_id" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.order_type_id" :disabled="form.updateStatus" class="w-100" :placeholder="$t('common.chooseSelect')" @change="onOrderTypeChange">
                                                <el-option :value="1" :label="$t('pages.ecommerce.checkout.order_types.PURCHASE')"></el-option>
                                                <el-option :value="2" :label="$t('pages.ecommerce.checkout.order_types.RENEWAL')"></el-option>
                                                <el-option :value="3" :label="$t('pages.ecommerce.checkout.order_types.UPGRADE')"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.currency_id') }}</label>
                                        <el-form-item prop="currency_id" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.currency_id" :disabled="form.updateStatus" filterable class="w-100" :placeholder="$t('common.chooseSelect')">
                                                <el-option v-for="c in $root.currencies" :key="c.id" :value="c.id" :label="`${c.code} - ${c.name}`"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.orderable_type') }}</label>
                                        <el-form-item prop="orderable_type" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.orderable_type" :disabled="form.updateStatus" class="w-100" :placeholder="$t('common.chooseSelect')" @change="onOrderableTypeChange">
                                                <el-option value="COMPANY" :label="$t('pages.ecommerce.checkout.orderable_types.COMPANY')"></el-option>
                                                <el-option value="CUSTOMER" :label="$t('pages.ecommerce.checkout.orderable_types.CUSTOMER')"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.orderable_id') }}</label>
                                        <el-form-item prop="orderable_id" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.orderable_id" :disabled="form.updateStatus || !form.data.orderable_type" filterable class="w-100" :placeholder="$t('common.chooseSelect')" @change="onOrderableIdChange">
                                                <el-option v-if="form.updateStatus && form.data.orderable_id" :value="form.data.orderable_id" :label="orderableDisplayLabel"></el-option>
                                                <template v-else>
                                                    <el-option v-for="item in orderableOptions" :key="item.id" :value="item.id" :label="formatOrderableLabel(item)"></el-option>
                                                </template>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3" v-if="isPurchase || isUpgrade">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.product_id') }}</label>
                                        <el-form-item prop="product_id" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.product_id" :disabled="form.updateStatus" filterable class="w-100" :placeholder="$t('common.chooseSelect')" @change="onProductChange">
                                                <el-option v-if="form.updateStatus && form.data.product_id" :value="form.data.product_id" :label="productDisplayLabel"></el-option>
                                                <template v-else>
                                                    <el-option v-for="p in availableProducts" :key="p.id" :value="p.id" :label="p.name + (p.code ? ' (' + p.code + ')' : '')"></el-option>
                                                </template>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3" v-if="isPurchase || isUpgrade">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.product_price_id') }}</label>
                                        <el-form-item prop="product_price_id">
                                            <el-select v-model="form.data.product_price_id" :disabled="form.updateStatus || !form.data.product_id" class="w-100" :placeholder="$t('common.chooseSelect')" clearable>
                                                <el-option v-if="form.updateStatus && form.data.product_price_id" :value="form.data.product_price_id" :label="priceDisplayLabel"></el-option>
                                                <template v-else>
                                                    <el-option v-for="pr in availablePrices" :key="pr.id" :value="pr.id" :label="formatPriceOption(pr)"></el-option>
                                                </template>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3" v-if="isRenewal">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.subscription_id') }}</label>
                                        <el-form-item prop="metadata.subscription_id" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.metadata.subscription_id" :disabled="form.updateStatus || !form.data.orderable_id" filterable class="w-100" :placeholder="$t('common.chooseSelect')">
                                                <el-option v-if="form.updateStatus && form.data.metadata.subscription_id" :value="form.data.metadata.subscription_id" :label="subscriptionDisplayLabel"></el-option>
                                                <template v-else>
                                                    <el-option v-for="s in availableSubscriptions" :key="s.id" :value="s.id" :label="formatSubscriptionOption(s)"></el-option>
                                                </template>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-6 fv-row mb-1 mt-3" v-if="isUpgrade">
                                        <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.replaces_subscription_id') }}</label>
                                        <el-form-item prop="metadata.replaces_subscription_id" :rules="$validation.getMessage(['required'])">
                                            <el-select v-model="form.data.metadata.replaces_subscription_id" :disabled="form.updateStatus || !form.data.orderable_id" filterable class="w-100" :placeholder="$t('common.chooseSelect')">
                                                <el-option v-if="form.updateStatus && form.data.metadata.replaces_subscription_id" :value="form.data.metadata.replaces_subscription_id" :label="replacesSubscriptionDisplayLabel"></el-option>
                                                <template v-else>
                                                    <el-option v-for="s in availableSubscriptions" :key="s.id" :value="s.id" :label="formatSubscriptionOption(s)"></el-option>
                                                </template>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <div class="col-md-12 fv-row mb-1 mt-3" v-if="!form.updateStatus">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.promotion_code') }}</label>
                                        <el-form-item prop="promotion_code">
                                            <el-input v-model="form.data.promotion_code" type="text" maxlength="64"/>
                                        </el-form-item>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <div class="col-md-3">
                        <div>
                            <div class="card mb-5" v-if="!form.updateStatus">
                                <div class="card-header border-bottom-dashed align-items-center">
                                    <h4 class="card-title m-0">
                                        {{ $t('pages.ecommerce.checkout.save.overrideCardTitle') }}
                                    </h4>
                                    <div class="card-toolbar">
                                        <el-switch v-model="priceOverrideEnabled" @change="onPriceOverrideToggle"/>
                                    </div>
                                </div>
                                <div class="card-body px-9 pt-4" v-if="priceOverrideEnabled">
                                    <div class="fv-row mb-1 mt-3">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.price_override') }}</label>
                                        <el-form-item prop="metadata.price_override">
                                            <el-input v-model="form.data.metadata.price_override" oninput="value=value.replace(/[^0-9.]/g,'')"/>
                                        </el-form-item>
                                    </div>

                                    <div class="fv-row mb-1 mt-3">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.fields.price_override_persistent') }}</label>
                                        <el-form-item prop="metadata.price_override_persistent">
                                            <el-select v-model="form.data.metadata.price_override_persistent" class="w-100" :placeholder="$t('common.chooseSelect')">
                                                <el-option :value="false" :label="$t('common.no')"></el-option>
                                                <el-option :value="true" :label="$t('common.yes')"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>

                                    <small class="text-muted d-block mt-1">{{ $t('pages.ecommerce.checkout.fields.price_override_help') }}</small>
                                </div>
                            </div>

                            <div class="card" v-if="form.updateStatus">
                                <div class="card-body">
                                    <div class="fv-row mb-1 mt-3" v-if="form.updateStatus">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.cols.status') }}</label>
                                        <div>
                                            <span :class="statusBadgeClass(form.data.status_id)" class="badge fs-7 px-3 py-2">{{ statusLabel(form.data.status_id) }}</span>
                                        </div>
                                    </div>

                                    <div class="fv-row mb-1 mt-3" v-if="form.updateStatus">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.cols.order_type') }}</label>
                                        <div>
                                            <span :class="orderTypeBadgeClass(form.data.order_type_id)" class="badge">{{ orderTypeLabel(form.data.order_type_id) }}</span>
                                        </div>
                                    </div>

                                    <div class="fv-row mb-1 mt-3" v-if="form.updateStatus">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.cols.final_amount') }}</label>
                                        <div class="fw-bolder fs-4">
                                            {{ formatPrice(form.data.final_amount) }}
                                            <span class="text-muted fs-6">{{ form.data.currency?.code }}</span>
                                        </div>
                                    </div>

                                    <div class="fv-row mb-1 mt-3" v-if="form.updateStatus && form.data.paid_at">
                                        <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.cols.paid_at') }}</label>
                                        <div>{{ $moment(form.data.paid_at).format("DD.MM.YYYY HH:mm") }}</div>
                                    </div>

                                    <div v-if="hasAnyAction" class="mt-5">
                                        <div class="separator border-bottom-dashed mb-4"></div>
                                        <label class="fs-6 fw-bold mb-3 d-block">{{ $t('pages.ecommerce.checkout.actions_title') }}</label>
                                        <div class="d-flex flex-column gap-2">
                                            <button v-if="canConfirm" @click.prevent="openConfirmModal" type="button" class="btn btn-sm btn-success">
                                                <i class="bi bi-check-lg me-1"></i>{{ $t('pages.ecommerce.checkout.actions.confirm') }}
                                            </button>

                                            <button v-if="canRequestRefund" @click.prevent="onRequestRefund" type="button" class="btn btn-sm btn-warning">
                                                <i class="bi bi-arrow-return-left me-1"></i>{{ $t('pages.ecommerce.checkout.actions.request_refund') }}
                                            </button>

                                            <button v-if="canProcessRefund" @click.prevent="openRefundModal" type="button" class="btn btn-sm btn-info">
                                                <i class="bi bi-cash-stack me-1"></i>{{ $t('pages.ecommerce.checkout.actions.process_refund') }}
                                            </button>

                                            <button v-if="canCancel" @click.prevent="onCancel" type="button" class="btn btn-sm btn-danger">
                                                <i class="bi bi-x-lg me-1"></i>{{ $t('pages.ecommerce.checkout.actions.cancel') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="fv-row mt-5" v-if="!form.updateStatus">
                                <el-form-item class="mb-0">
                                    <button @click.prevent="onSubmit" :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary w-100" type="button">
                                        <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                        <span v-if="form.loading" class="indicator-progress">
                                            {{ $t("messages.wait") }}
                                            <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </span>
                                    </button>
                                </el-form-item>
                            </div>
                        </div>
                    </div>
                </div>
            </el-form>
        </el-tab-pane>

        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.ecommerce.checkout.save.tabs.items')" name="items">
            <custom-table
                :title="$t('pages.ecommerce.checkout.save.tabs.items')"
                rowKey="id"
                :items="form.data.items || []"
                :loading="false"
                :emptyTableText="$t('pages.ecommerce.checkout.items.no_items')"
                :columns="itemFields"
                :actions="tabActions"
                @action="onTabRefresh">
                <template v-slot:title="{ row: record }">
                    <div class="d-flex flex-column">
                        <span class="fw-bold">{{ record.title }}</span>
                        <span v-if="record.itemable?.code" class="text-muted fs-8">{{ record.itemable.code }}</span>
                    </div>
                </template>
                <template v-slot:unit_price="{ row: record }">
                    {{ formatPrice(record.unit_price) }}
                </template>
                <template v-slot:total_price="{ row: record }">
                    {{ formatPrice(record.total_price) }}
                </template>
                <template v-slot:tax_amount="{ row: record }">
                    {{ formatPrice(itemTaxTotal(record)) }}
                    <span v-if="record.taxes?.length" class="text-muted fs-8 d-block">{{ record.taxes.map(t => t.tax_name).join(', ') }}</span>
                </template>
            </custom-table>
        </el-tab-pane>

        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.ecommerce.checkout.save.tabs.transactions')" name="transactions">
            <custom-table
                :title="$t('pages.ecommerce.checkout.save.tabs.transactions')"
                rowKey="id"
                :items="form.data.transactions || []"
                :loading="false"
                :emptyTableText="$t('pages.ecommerce.checkout.transactions.no_transactions')"
                :columns="transactionFields"
                :actions="tabActions"
                @action="onTabRefresh">
                <template v-slot:type="{ row: record }">
                    <span :class="transactionTypeBadgeClass(record.transaction_type_id)" class="badge">{{ record.transaction_type?.label || transactionTypeLabel(record.transaction_type_id) }}</span>
                </template>
                <template v-slot:status="{ row: record }">
                    <span :class="transactionStatusBadgeClass(record.status_id)" class="badge">{{ record.status?.label || transactionStatusLabel(record.status_id) }}</span>
                </template>
                <template v-slot:payment_method="{ row: record }">
                    {{ record.payment_method?.name || '-' }}
                </template>
                <template v-slot:amount="{ row: record }">
                    {{ formatPrice(record.amount) }}
                </template>
                <template v-slot:reference="{ row: record }">
                    <span class="text-muted fs-8">{{ record.reference_code }}</span>
                    <span v-if="record.provider_reference" class="d-block fs-8">{{ record.provider_reference }}</span>
                </template>
                <template v-slot:created_at="{ row: record }">
                    {{ record.created_at ? $moment(record.created_at).format("DD.MM.YYYY HH:mm") : '-' }}
                </template>
            </custom-table>
        </el-tab-pane>

        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.ecommerce.checkout.save.tabs.promotions')" name="promotions">
            <custom-table
                :title="$t('pages.ecommerce.checkout.save.tabs.promotions')"
                rowKey="id"
                :items="form.data.promotions || []"
                :loading="false"
                :emptyTableText="$t('pages.ecommerce.checkout.promotions.no_promotions')"
                :columns="promotionFields"
                :actions="tabActions"
                @action="onTabRefresh">
                <template v-slot:code="{ row: record }">
                    {{ record.promotion_code }}
                </template>
                <template v-slot:type="{ row: record }">
                    {{ record.discount_type?.label || '-' }}
                </template>
                <template v-slot:value="{ row: record }">
                    <template v-if="record.promotion_discount_type_id === 1">%{{ record.promotion_discount_value }}</template>
                    <template v-else>{{ formatPrice(record.promotion_discount_value) }}</template>
                </template>
                <template v-slot:amount="{ row: record }">
                    {{ formatPrice(record.discount_amount) }}
                </template>
            </custom-table>
        </el-tab-pane>

        <el-tab-pane v-if="form.updateStatus" :label="$t('pages.ecommerce.checkout.save.tabs.notes')" name="notes">
            <div class="row g-6 mb-6 g-xl-9 mb-xl-9 unset-position-card">
                <div class="col-md-9">
                    <div class="card mb-5">
                        <div class="card-header border-bottom-dashed align-items-center">
                            <h4 class="card-title m-0">{{ $t('pages.ecommerce.checkout.save.tabs.notes') }}</h4>
                            <div class="card-toolbar">
                                <el-tooltip effect="dark" :content="$t('btn.refresh')" placement="top">
                                    <el-button size="small" @click="loadData">
                                        <i class="bi bi-arrow-repeat"></i>
                                    </el-button>
                                </el-tooltip>
                            </div>
                        </div>
                        <div class="card-body px-9 pt-4">
                            <div v-if="!notes.length" class="text-muted text-center py-4">
                                {{ $t('pages.ecommerce.checkout.notes.no_notes') }}
                            </div>
                            <div v-else class="d-flex flex-column gap-3">
                                <div v-for="note in notes" :key="note.id" class="d-flex gap-3 align-items-start">
                                    <div class="flex-grow-1">
                                        <div class="d-flex align-items-center gap-2 mb-1">
                                            <span :class="actorBadgeClass(note.actor_type)" class="badge fs-9">{{ actorLabel(note) }}</span>
                                            <span class="text-muted fs-8">{{ $moment(note.created_at).format("DD.MM.YYYY HH:mm") }}</span>
                                        </div>
                                        <div class="text-gray-800" style="white-space: pre-wrap;">{{ note.note }}</div>
                                    </div>
                                    <a @click="onDeleteNote(note)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm flex-shrink-0">
                                        <span class="svg-icon svg-icon-3">
                                            <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                                        </span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-3">
                    <div class="card">
                        <div class="card-header border-bottom-dashed align-items-center">
                            <h4 class="card-title m-0">{{ $t('pages.ecommerce.checkout.notes.add_card_title') }}</h4>
                        </div>
                        <div class="card-body px-9 pt-4">
                            <div class="fv-row mb-1 mt-3">
                                <el-input v-model="newNote" :placeholder="$t('pages.ecommerce.checkout.notes.placeholder')" type="textarea" :rows="4"/>
                            </div>
                            <button @click="onAddNote" :disabled="!newNote.trim() || noteLoading" type="button" class="btn btn-primary w-100 mt-3">
                                <i class="bi bi-plus-lg me-1"></i>{{ $t('pages.ecommerce.checkout.notes.add_btn') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </el-tab-pane>
    </el-tabs>

    <div class="modal fade" id="kt_modal_checkout_confirm" ref="confirmModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ $t('pages.ecommerce.checkout.confirm_modal.title') }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onConfirmSubmit" :model="confirm" ref="confirmForm">
                    <div class="modal-body py-10 px-lg-17">
                        <div v-if="form.data.status_id === 6" class="alert alert-light-warning d-flex flex-column gap-1 py-2 mb-4">
                            <div class="d-flex justify-content-between"><span>{{ $t('pages.ecommerce.checkout.confirm_modal.paid_total_label') }}:</span><span class="fw-bold">{{ formatPrice(paidTotal) }} {{ form.data.currency?.code }}</span></div>
                            <div class="d-flex justify-content-between"><span>{{ $t('pages.ecommerce.checkout.confirm_modal.remaining_label') }}:</span><span class="fw-bolder">{{ formatPrice(remainingAmount) }} {{ form.data.currency?.code }}</span></div>
                        </div>

                        <div class="fv-row mb-3">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.confirm_modal.amount_label') }}</label>
                            <el-form-item prop="amount">
                                <el-input v-model="confirm.amount" oninput="value=value.replace(/[^0-9.]/g,'')" :placeholder="formatPrice(remainingAmount)">
                                    <template #append>
                                        <el-button @click="fillFullAmount">{{ $t('pages.ecommerce.checkout.confirm_modal.amount_fill_all') }}</el-button>
                                    </template>
                                </el-input>
                            </el-form-item>
                            <small class="text-muted d-block mt-1">{{ $t('pages.ecommerce.checkout.confirm_modal.amount_help') }}</small>
                        </div>

                        <div class="fv-row mb-3">
                            <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.confirm_modal.payment_method_label') }}</label>
                            <el-form-item prop="payment_method_id" :rules="$validation.getMessage(['required'])">
                                <el-select v-model="confirm.payment_method_id" filterable class="w-100" :placeholder="$t('common.chooseSelect')">
                                    <el-option v-for="m in paymentMethods" :key="m.id" :value="m.id" :label="m.name"></el-option>
                                </el-select>
                            </el-form-item>
                        </div>

                        <div class="fv-row mb-3">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.confirm_modal.provider_reference_label') }}</label>
                            <el-form-item prop="provider_reference">
                                <el-input v-model="confirm.provider_reference" type="text" maxlength="128"/>
                            </el-form-item>
                        </div>

                        <div class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.confirm_modal.note_label') }}</label>
                            <el-form-item prop="note">
                                <el-input v-model="confirm.note" type="textarea" rows="2"/>
                            </el-form-item>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="confirm.loading ? 'on' : null" class="btn btn-lg btn-success" type="submit" :disabled="confirm.loading">
                            <span v-if="!confirm.loading" class="indicator-label">{{ $t("btn.confirm") }}</span>
                            <span v-if="confirm.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>

    <div class="modal fade" id="kt_modal_checkout_refund" ref="refundModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ $t('pages.ecommerce.checkout.refund_modal.title') }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onRefundSubmit" :model="refund" ref="refundForm">
                    <div class="modal-body py-10 px-lg-17">
                        <div class="alert alert-light-danger py-2 mb-4">{{ $t('pages.ecommerce.checkout.refund_modal.warning') }}</div>

                        <div class="fv-row mb-3">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.refund_modal.payment_method_label') }}</label>
                            <el-form-item prop="payment_method_id">
                                <el-select v-model="refund.payment_method_id" filterable class="w-100" :placeholder="$t('common.chooseSelect')" clearable>
                                    <el-option v-for="m in paymentMethods" :key="m.id" :value="m.id" :label="m.name"></el-option>
                                </el-select>
                            </el-form-item>
                            <small class="text-muted d-block mt-1">{{ $t('pages.ecommerce.checkout.refund_modal.payment_method_help') }}</small>
                        </div>

                        <div class="fv-row mb-3">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.refund_modal.provider_reference_label') }}</label>
                            <el-form-item prop="provider_reference">
                                <el-input v-model="refund.provider_reference" type="text" maxlength="128"/>
                            </el-form-item>
                        </div>

                        <div class="fv-row mb-1">
                            <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.checkout.refund_modal.note_label') }}</label>
                            <el-form-item prop="note">
                                <el-input v-model="refund.note" type="textarea" rows="2"/>
                            </el-form-item>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="refund.loading ? 'on' : null" class="btn btn-lg btn-info" type="submit" :disabled="refund.loading">
                            <span v-if="!refund.loading" class="indicator-label">{{ $t('pages.ecommerce.checkout.actions.process_refund') }}</span>
                            <span v-if="refund.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import PageHeader from "@/components/page-header/PageHeader.vue";
import CustomTable from "@/components/custom-table";
import {fetchByIds} from "@/core/services/Helper";

const STATUS_CODE_MAP = {
    0: 'PENDING', 1: 'AWAITING_PAYMENT', 2: 'PAID', 3: 'FAILED',
    4: 'CANCELLED', 5: 'REFUNDED', 6: 'PARTIALLY_PAID', 7: 'REFUND_REQUESTED',
};
const STATUS_BADGE_MAP = {
    0: 'badge-light-warning',
    1: 'badge-light-warning',
    2: 'badge-light-success',
    3: 'badge-light-danger',
    4: 'badge-light-dark',
    5: 'badge-light-info',
    6: 'badge-light-warning',
    7: 'badge-light-info',
};
const ORDER_TYPE_CODE_MAP = {1: 'PURCHASE', 2: 'RENEWAL', 3: 'UPGRADE'};
const ORDER_TYPE_BADGE_MAP = {1: 'badge-light-primary', 2: 'badge-light-info', 3: 'badge-light-success'};
const TRANSACTION_TYPE_CODE_MAP = {1: 'PAYMENT', 2: 'REFUND'};
const TRANSACTION_TYPE_BADGE_MAP = {1: 'badge-light-success', 2: 'badge-light-warning'};
const TRANSACTION_STATUS_CODE_MAP = {0: 'PENDING', 1: 'PROCESSING', 2: 'COMPLETED', 3: 'FAILED'};
const TRANSACTION_STATUS_BADGE_MAP = {0: 'badge-light-warning', 1: 'badge-light-info', 2: 'badge-light-success', 3: 'badge-light-danger'};

export default {
    name: "_id",
    components: {PageHeader, CustomTable},
    data() {
        return {
            tabModel: 'general',
            priceOverrideEnabled: false,
            newNote: '',
            noteLoading: false,
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                data: this.defaultFormData(),
            },
            confirm: {
                loading: false,
                amount: '',
                payment_method_id: undefined,
                provider_reference: '',
                note: '',
            },
            refund: {
                loading: false,
                payment_method_id: undefined,
                provider_reference: '',
                note: '',
            },
            itemFields: [
                {name: this.$t("pages.ecommerce.checkout.items.cols.title"), scopedSlots: {customRender: "title"}},
                {name: this.$t("pages.ecommerce.checkout.items.cols.quantity"), key: "quantity"},
                {name: this.$t("pages.ecommerce.checkout.items.cols.unit_price"), scopedSlots: {customRender: "unit_price"}},
                {name: this.$t("pages.ecommerce.checkout.items.cols.tax_amount"), scopedSlots: {customRender: "tax_amount"}},
                {name: this.$t("pages.ecommerce.checkout.items.cols.total_price"), scopedSlots: {customRender: "total_price"}},
            ],
            transactionFields: [
                {name: this.$t("pages.ecommerce.checkout.transactions.cols.type"), scopedSlots: {customRender: "type"}},
                {name: this.$t("pages.ecommerce.checkout.transactions.cols.status"), scopedSlots: {customRender: "status"}},
                {name: this.$t("pages.ecommerce.checkout.transactions.cols.payment_method"), scopedSlots: {customRender: "payment_method"}},
                {name: this.$t("pages.ecommerce.checkout.transactions.cols.amount"), scopedSlots: {customRender: "amount"}},
                {name: this.$t("pages.ecommerce.checkout.transactions.cols.reference"), scopedSlots: {customRender: "reference"}},
                {name: this.$t("pages.ecommerce.checkout.transactions.cols.created_at"), scopedSlots: {customRender: "created_at"}},
            ],
            promotionFields: [
                {name: this.$t("pages.ecommerce.checkout.promotions.cols.code"), scopedSlots: {customRender: "code"}},
                {name: this.$t("pages.ecommerce.checkout.promotions.cols.type"), scopedSlots: {customRender: "type"}},
                {name: this.$t("pages.ecommerce.checkout.promotions.cols.value"), scopedSlots: {customRender: "value"}},
                {name: this.$t("pages.ecommerce.checkout.promotions.cols.amount"), scopedSlots: {customRender: "amount"}},
            ],
            tabActions: [
                {name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh"},
            ],
        }
    },
    computed: {
        itemID() {
            return this.$route.params.id;
        },
        save() {
            return this.$store.state.ecommerce.checkout.save.data;
        },
        notes() {
            return this.form.data.notes || [];
        },
        isPurchase() { return this.form.data.order_type_id === 1; },
        isRenewal() { return this.form.data.order_type_id === 2; },
        isUpgrade() { return this.form.data.order_type_id === 3; },
        allProducts() {
            return this.$store.state.ecommerce.product.table?.data || [];
        },
        availableProducts() {
            if (!this.form.data.orderable_type) return this.allProducts;
            return this.allProducts.filter(p => p.target_type === this.form.data.orderable_type);
        },
        selectedProduct() {
            return this.allProducts.find(p => p.id === this.form.data.product_id) || null;
        },
        availablePrices() {
            return this.selectedProduct?.prices || [];
        },
        companies() {
            return this.$store.state.company.table?.data || [];
        },
        users() {
            return this.$store.state.user.table?.data || [];
        },
        orderableOptions() {
            if (this.form.data.orderable_type === 'COMPANY') return this.companies;
            if (this.form.data.orderable_type === 'CUSTOMER') return this.users;
            return [];
        },
        availableSubscriptions() {
            return this.$store.state.ecommerce.subscription.table?.data || [];
        },
        paymentMethods() {
            return this.$store.state.ecommerce.payment_method?.table?.data || [];
        },
        canConfirm() {
            return this.form.updateStatus && [0, 1, 6].includes(this.form.data.status_id);
        },
        canCancel() {
            return this.form.updateStatus && [0, 1, 6].includes(this.form.data.status_id);
        },
        canRequestRefund() {
            return this.form.updateStatus && [2, 6].includes(this.form.data.status_id);
        },
        canProcessRefund() {
            return this.form.updateStatus && this.form.data.status_id === 7;
        },
        hasAnyAction() {
            return this.canConfirm || this.canCancel || this.canRequestRefund || this.canProcessRefund;
        },
        paidTotal() {
            const txns = this.form.data.transactions || [];
            return txns
                .filter(t => t.transaction_type_id === 1 && t.status_id === 2)
                .reduce((sum, t) => sum + parseFloat(t.paid_amount || 0), 0);
        },
        remainingAmount() {
            return Math.max(0, parseFloat(this.form.data.final_amount || 0) - this.paidTotal);
        },
        orderableDisplayLabel() {
            const o = this.form.data.orderable;
            if (o) return this.formatOrderableLabel(o);
            return '#' + this.form.data.orderable_id;
        },
        productDisplayLabel() {
            const p = this.form.data.product;
            if (p) return p.name + (p.code ? ' (' + p.code + ')' : '');
            return '#' + this.form.data.product_id;
        },
        priceDisplayLabel() {
            const m = this.form.data.product_item_metadata;
            if (m) {
                const periodLabel = (m.is_recurring && m.period_type_id)
                    ? this.$t('pages.ecommerce.product.price.periods[' + (m.period_type_id - 1) + ']')
                    : this.$t('pages.ecommerce.product.price.is_recurring_oneshot');
                const price = m.original_price ?? m.price_override ?? '';
                const currencyCode = this.form.data.currency?.code || '';
                return `${price} ${currencyCode} - ${periodLabel}`;
            }
            return '#' + this.form.data.product_price_id;
        },
        subscriptionDisplayLabel() {
            const s = this.form.data.subscription;
            if (s) return this.formatSubscriptionOption(s);
            return '#' + this.form.data.metadata?.subscription_id;
        },
        replacesSubscriptionDisplayLabel() {
            const s = this.form.data.replaces_subscription;
            if (s) return this.formatSubscriptionOption(s);
            return '#' + this.form.data.metadata?.replaces_subscription_id;
        },
    },
    created() {
        if (this.itemID && !(this.itemID > 0)) {
            this.$router.push({path: "/ecommerce/checkout"});
        }
    },
    mounted() {
        if (this.itemID && this.itemID > 0) {
            this.setCurrentPageBreadcrumbs(this.sprintf(this.$t('pages.ecommerce.checkout.editOrderPattern'), [this.itemID]), []);
            this.form.updateStatus = true;
            this.loadData();
        } else {
            this.setCurrentPageBreadcrumbs(this.$t('pages.ecommerce.checkout.newOrder'), []);
            this.$store.dispatch('ecommerce/product/get', {filterData: {page_limit: 9999}});
        }
    },
    watch: {
        itemID(newValue) {
            if (newValue && newValue > 0) {
                this.form.updateStatus = true;
                this.loadData(newValue);
                this.setCurrentPageBreadcrumbs(this.sprintf(this.$t('pages.ecommerce.checkout.editOrderPattern'), [newValue]), []);
            } else {
                this.resetForm();
                this.setCurrentPageBreadcrumbs(this.$t('pages.ecommerce.checkout.newOrder'), []);
            }
        },
    },
    methods: {
        defaultFormData() {
            return {
                order_type_id: 1,
                orderable_type: undefined,
                orderable_id: undefined,
                currency_id: undefined,
                product_id: undefined,
                product_price_id: undefined,
                metadata: {
                    subscription_id: undefined,
                    replaces_subscription_id: undefined,
                    price_override: undefined,
                    price_override_persistent: false,
                },
                promotion_code: '',
            };
        },
        resetForm() {
            this.tabModel = 'general';
            this.form.updateStatus = false;
            this.form.data = this.defaultFormData();
            this.priceOverrideEnabled = false;
            this.newNote = '';
        },
        async loadData(id = this.itemID) {
            await this.fetchOrder(id);
        },
        async fetchOrder(id = this.itemID) {
            await this.$store.dispatch("ecommerce/checkout/show", {id});
            const data = this.save;
            const productItem = (data.items || []).find(i => i.itemable_type === 'PRODUCT');

            const subIds = [data.metadata?.subscription_id, data.metadata?.replaces_subscription_id].filter(Boolean);
            const subs = await fetchByIds(this.axios, this.endpoints['ecommerce_subscription'], subIds);
            const subById = Object.fromEntries(subs.map(s => [s.id, s]));

            this.form.data = {
                ...data,
                metadata: data.metadata || {},
                product_id: productItem?.itemable_id,
                product: productItem?.itemable,
                product_price_id: productItem?.metadata?.product_price_id,
                product_item_metadata: productItem?.metadata,
                subscription: subById[data.metadata?.subscription_id],
                replaces_subscription: subById[data.metadata?.replaces_subscription_id],
            };
        },
        onTabRefresh(name) {
            if (name === 'refresh') {
                this.loadData();
            }
        },
        fillFullAmount() {
            this.confirm.amount = String(this.remainingAmount);
        },
        statusLabel(statusId) {
            return this.$t('pages.ecommerce.checkout.statuses.' + (STATUS_CODE_MAP[statusId] || 'PENDING'));
        },
        statusBadgeClass(statusId) {
            return STATUS_BADGE_MAP[statusId] || 'badge-secondary';
        },
        orderTypeLabel(typeId) {
            return this.$t('pages.ecommerce.checkout.order_types.' + (ORDER_TYPE_CODE_MAP[typeId] || 'PURCHASE'));
        },
        orderTypeBadgeClass(typeId) {
            return ORDER_TYPE_BADGE_MAP[typeId] || 'badge-light-primary';
        },
        transactionTypeLabel(typeId) {
            return this.$t('pages.ecommerce.checkout.transactions.types.' + (TRANSACTION_TYPE_CODE_MAP[typeId] || 'PAYMENT'));
        },
        transactionTypeBadgeClass(typeId) {
            return TRANSACTION_TYPE_BADGE_MAP[typeId] || 'badge-secondary';
        },
        transactionStatusLabel(statusId) {
            return this.$t('pages.ecommerce.checkout.transactions.statuses.' + (TRANSACTION_STATUS_CODE_MAP[statusId] || 'PENDING'));
        },
        transactionStatusBadgeClass(statusId) {
            return TRANSACTION_STATUS_BADGE_MAP[statusId] || 'badge-secondary';
        },
        actorLabel(note) {
            if (note.actor && (note.actor.firstname || note.actor.lastname)) {
                return ((note.actor.firstname || '') + ' ' + (note.actor.lastname || '')).trim();
            }
            return this.$t('pages.ecommerce.checkout.notes.actor_types.' + (note.actor_type || 'SYSTEM'));
        },
        actorBadgeClass(actorType) {
            if (actorType === 'ADMIN') return 'badge-light-success';
            if (actorType === 'CUSTOMER') return 'badge-light-info';
            return 'badge-secondary';
        },
        formatPrice(price) {
            const value = parseFloat(price);
            if (isNaN(value)) return price;
            return value.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        formatPriceOption(price) {
            const periodLabel = price.period_type_id
                ? this.$t('pages.ecommerce.product.price.periods[' + (price.period_type_id - 1) + ']')
                : this.$t('pages.ecommerce.product.price.is_recurring_oneshot');
            const currency = price.currency?.code || '';
            return `${price.price} ${currency} - ${periodLabel}`;
        },
        formatOrderableLabel(item) {
            if (this.form.data.orderable_type === 'COMPANY') return item.name;
            const fn = item.firstname || '';
            const ln = item.lastname || '';
            return (fn + ' ' + ln).trim() || item.email || ('#' + item.id);
        },
        formatSubscriptionOption(s) {
            const productName = s.product?.name || '';
            const status = s.status_id === 1 ? this.$t('pages.ecommerce.subscription.statuses.ACTIVE')
                : s.status_id === 2 ? this.$t('pages.ecommerce.subscription.statuses.EXPIRED')
                : '';
            return `#${s.id} - ${productName}${status ? ' (' + status + ')' : ''}`;
        },
        itemTaxTotal(item) {
            return (item.taxes || []).reduce((sum, t) => sum + parseFloat(t.tax_amount || 0), 0);
        },
        onOrderTypeChange() {
            this.form.data.product_id = undefined;
            this.form.data.product_price_id = undefined;
            this.form.data.metadata.subscription_id = undefined;
            this.form.data.metadata.replaces_subscription_id = undefined;
        },
        onOrderableTypeChange(val) {
            this.form.data.orderable_id = undefined;
            this.form.data.product_id = undefined;
            this.form.data.product_price_id = undefined;
            this.form.data.metadata.subscription_id = undefined;
            this.form.data.metadata.replaces_subscription_id = undefined;
            if (val === 'COMPANY') {
                this.$store.dispatch('company/get', {page: {pageSize: 9999}});
            } else if (val === 'CUSTOMER') {
                this.$store.dispatch('user/get', {page: {pageSize: 9999}});
            }
        },
        onOrderableIdChange(val) {
            if (val && (this.isRenewal || this.isUpgrade)) {
                this.$store.dispatch('ecommerce/subscription/get', {
                    filterData: {
                        subscribable_type: this.form.data.orderable_type,
                        subscribable_id: val,
                        page_limit: 9999,
                    }
                });
            }
        },
        onProductChange() {
            this.form.data.product_price_id = undefined;
        },
        onPriceOverrideToggle(val) {
            if (!val) {
                this.form.data.metadata.price_override = undefined;
                this.form.data.metadata.price_override_persistent = false;
            }
        },
        onSubmit() {
            this.$refs.dataForm.validate(async (valid) => {
                if (!valid) return false;

                this.form.loading = true;

                const payload = {
                    order_type_id: this.form.data.order_type_id,
                    orderable_type: this.form.data.orderable_type,
                    orderable_id: this.form.data.orderable_id,
                    currency_id: this.form.data.currency_id,
                };

                if (this.isPurchase || this.isUpgrade) {
                    payload.product_id = this.form.data.product_id;
                    if (this.form.data.product_price_id) payload.product_price_id = this.form.data.product_price_id;
                }

                payload.metadata = {};
                if (this.isRenewal) payload.metadata.subscription_id = this.form.data.metadata.subscription_id;
                if (this.isUpgrade) payload.metadata.replaces_subscription_id = this.form.data.metadata.replaces_subscription_id;
                if (this.priceOverrideEnabled && this.form.data.metadata.price_override) {
                    payload.metadata.price_override = parseFloat(this.form.data.metadata.price_override);
                    payload.metadata.price_override_persistent = !!this.form.data.metadata.price_override_persistent;
                }

                if (this.form.data.promotion_code) payload.promotion_code = this.form.data.promotion_code;

                try {
                    const response = await this.$store.dispatch('ecommerce/checkout/add', {formData: payload});
                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.$router.push({path: "/ecommerce/checkout/save/" + response.data.id});
                        });
                    }
                } finally {
                    this.form.loading = false;
                }
            });
        },
        onCancel() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.ecommerce.checkout.actions.cancel'),
                message: this.$t('pages.ecommerce.checkout.confirms.cancel'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/checkout/cancel', {id: this.form.data.id});
                    if (response?.status) this.onResponse(response, () => this.loadData());
                },
            });
        },
        onRequestRefund() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.ecommerce.checkout.actions.request_refund'),
                message: this.$t('pages.ecommerce.checkout.confirms.request_refund'),
                confirmType: 'primary',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/checkout/requestRefund', {id: this.form.data.id});
                    if (response?.status) this.onResponse(response, () => this.loadData());
                },
            });
        },
        openConfirmModal() {
            this.confirm = {
                loading: false,
                amount: '',
                payment_method_id: undefined,
                provider_reference: '',
                note: '',
            };
            this.$store.dispatch('ecommerce/payment_method/get', {page: {pageSize: 9999}});
            this.showModal(this.$refs.confirmModal);
        },
        onConfirmSubmit() {
            this.$refs.confirmForm.validate(async (valid) => {
                if (!valid) return false;

                this.confirm.loading = true;

                const payload = {
                    payment_method_id: this.confirm.payment_method_id,
                };
                if (this.confirm.amount) payload.amount = parseFloat(this.confirm.amount);
                if (this.confirm.provider_reference) payload.provider_reference = this.confirm.provider_reference;
                if (this.confirm.note) payload.note = this.confirm.note;

                try {
                    const response = await this.$store.dispatch('ecommerce/checkout/confirm', {
                        id: this.form.data.id,
                        formData: payload,
                    });
                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.hideModal(this.$refs.confirmModal);
                            this.loadData();
                        });
                    }
                } finally {
                    this.confirm.loading = false;
                }
            });
        },
        openRefundModal() {
            this.refund = {
                loading: false,
                payment_method_id: undefined,
                provider_reference: '',
                note: '',
            };
            this.$store.dispatch('ecommerce/payment_method/get', {page: {pageSize: 9999}});
            this.showModal(this.$refs.refundModal);
        },
        onRefundSubmit() {
            this.$refs.refundForm.validate(async (valid) => {
                if (!valid) return false;

                this.refund.loading = true;

                const payload = {};
                if (this.refund.payment_method_id) payload.payment_method_id = this.refund.payment_method_id;
                if (this.refund.provider_reference) payload.provider_reference = this.refund.provider_reference;
                if (this.refund.note) payload.note = this.refund.note;

                try {
                    const response = await this.$store.dispatch('ecommerce/checkout/processRefund', {
                        id: this.form.data.id,
                        formData: payload,
                    });
                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.hideModal(this.$refs.refundModal);
                            this.loadData();
                        });
                    }
                } finally {
                    this.refund.loading = false;
                }
            });
        },
        async onAddNote() {
            const text = this.newNote.trim();
            if (!text) return;

            this.noteLoading = true;
            try {
                const response = await this.$store.dispatch('ecommerce/checkout/note/add', {
                    checkoutOrderId: this.form.data.id,
                    formData: {note: text},
                });
                if (response?.status) {
                    this.newNote = '';
                    this.loadData();
                }
            } finally {
                this.noteLoading = false;
            }
        },
        onDeleteNote(note) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('btn.delete'),
                message: this.$t('pages.ecommerce.checkout.notes.delete_confirm'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/checkout/note/delete', {
                        checkoutOrderId: this.form.data.id,
                        id: note.id,
                    });
                    if (response?.status) {
                        this.loadData();
                    }
                },
            });
        },
    }
}
</script>
